import { parseApiDate } from "@vyne-shared/utils/date";

const synth: unique symbol = Symbol("paymentReportPosting synth");

/** Posting state of a transaction (raw object form). */
export interface PaymentReportPostingRaw {
    /** Posting kind of a transaction. */
    kind: "WriteBackManual" | "WriteBackAutomated" | "MarkManual" | "MarkManualRefund";

    /**
     * Status of a particular writeback operation (command), or mark-manual operation.
     *
     * For mark-manual operations, the API is guaranteed to return "Success" (although in the UI, we do synthesize
     * a Pending status for mark-manual operations with the API call in progress, to facilitate code reuse).
     */
    status: StatusByGroup<"PendingOrCancelling"> | StatusByGroup<"Success"> | StatusByGroup<"FailureOrOverdue">;

    /**
     * Posted date in YYYY-MM-DD format for manual. Time in hh:mm:ss format included if writeback.
     *
     * Guaranteed to be present for any status other than "Pending".
     * Guaranteed to be null for "Pending" status.
     */
    postedDate: string | null;

    /** Indicates that the posting state has been synthesized within the UI. */
    [synth]?: boolean;
}

/** Posting state of a transaction (class union form). */
export type PaymentReportPosting = PaymentReportPending | PaymentReportSuccess | PaymentReportFailed;

const groups = Object.freeze({
    PendingOrCancelling: Object.freeze(["Pending", "CancellationRequested"] as const),
    Success: Object.freeze(["Success"] as const),
    FailureOrOverdue: Object.freeze(["Failure", "Overdue", "Cancelled"] as const),
} as const);

type Groups = typeof groups;
type Group = keyof Groups;
type StatusByGroup<G extends Group> = Groups[G][number];

/** Base class providing functionality for inspecting posting data. */
export abstract class PaymentReportPostingAbstract<G extends Group> implements PaymentReportPostingRaw {
    readonly kind;
    readonly status: StatusByGroup<G>;
    readonly postedDate;

    /** The group of statuses the concrete class handles. */
    abstract readonly group: G;

    /** Indicates that the posting state has been synthesized within the UI. */
    readonly isSynthetic: boolean;

    constructor({ kind, status, postedDate, [synth]: isSynthetic }: PaymentReportPostingRaw) {
        this.kind = kind;
        this.status = status;
        this.postedDate = postedDate;
        this.isSynthetic = !!isSynthetic;
    }

    /** Validate the state of this posting object. */
    assert() {
        if (!isGroup(this.group, this.status)) throw new Error(`invalid status ${this.status} for group ${this.group}`);
    }

    /** Indicates whether this posting represents a manual mark-as-posted. */
    get manualMarking() {
        return ["MarkManual", "MarkManualRefund"].includes(this.kind);
    }

    /** Determine whether this posting has the specified status and is synthetic (a fake UI record). */
    isSyntheticStatus(status: StatusByGroup<G>) {
        return this.isSynthetic && this.status === status;
    }

    /** Determine whether this posting has the specified status and is not synthetic (real value from the back-end). */
    isNonSyntheticStatus(status: StatusByGroup<G>) {
        return !this.isSynthetic && this.status === status;
    }

    /** Get the point in time this status was assigned. */
    abstract get postedDateMs(): number | null;
}

/** Represents posting data when the post is pending. */
export class PaymentReportPending extends PaymentReportPostingAbstract<"PendingOrCancelling"> {
    override readonly group = "PendingOrCancelling";

    constructor(raw: PaymentReportPostingRaw) {
        super(raw);
        this.assert();
    }

    /**
     * Build a {@link PaymentReportPending} representing a row in a pending (or cancellation requested) state.
     *
     * Note that the API never actually returns "Pending" for `MarkManual`.
     */
    static synthesize(kind: "MarkManual" | "WriteBackManual", status?: StatusByGroup<"PendingOrCancelling">) {
        return Object.freeze(
            new PaymentReportPending({ kind, status: status ?? "Pending", postedDate: null, [synth]: true }),
        );
    }

    override get postedDateMs() {
        return null as number | null;
    }
}

type Dated = Exclude<Group, "PendingOrCancelling">;

/** Further base class when the posting data includes a date. */
export abstract class PaymentReportDated<G extends Dated = Dated> extends PaymentReportPostingAbstract<G> {
    override get postedDateMs() {
        return parseApiDate(this.postedDate)!;
    }
}

/** Represents posting data when the post was successful. */
export class PaymentReportSuccess extends PaymentReportDated<"Success"> {
    override readonly group = "Success";

    constructor(raw: PaymentReportPostingRaw) {
        super(raw);
        this.assert();
    }
}

/** Represents posting data when the post failed (due to an error or being overdue). */
export class PaymentReportFailed extends PaymentReportDated<"FailureOrOverdue"> {
    override readonly group = "FailureOrOverdue";

    constructor(raw: PaymentReportPostingRaw) {
        super(raw);
        this.assert();
    }
}

/** Factory to transform raw payment report posting data to a class instance. */
export function cookPaymentReportPosting(raw: PaymentReportPostingRaw) {
    const { status } = raw;

    return Object.freeze(
        new (isGroup("PendingOrCancelling", status)
            ? PaymentReportPending
            : isGroup("Success", status)
              ? PaymentReportSuccess
              : PaymentReportFailed)(raw),
    );
}

/** Determine whether the provided status belongs to the provided group. */
function isGroup(group: Group, status: PaymentReportPostingRaw["status"]) {
    const ar: readonly StatusByGroup<Group>[] = groups[group];
    return ar.includes(status);
}
